import React, { useState } from 'react';

// 5-step first-run onboarding for the FET reading section.
// Persists user preferences (level + Arabic toggle) to localStorage and sets
// fet_reading_onboarded so the flow is shown once per device.

const READING_PARTS_PREVIEW = [
  { n: 1, title: 'Signs & Notices', qs: 7, marks: 7, color: '#6E77D8' },
  { n: 2, title: 'Gap Fill',        qs: 6, marks: 6, color: '#3E6FB0' },
  { n: 3, title: 'Text Matching',   qs: 6, marks: 6, color: '#A86FB3' },
  { n: 4, title: 'People ↔ Place',  qs: 6, marks: 6, color: '#4E8A5E' },
  { n: 5, title: 'Long Reading',    qs: 5, marks: 5, color: '#B55A48' },
];

export default function ReadingOnboarding({ theme, isMobile, navigate }) {
  const [step, setStep] = useState(0);
  const [level, setLevel] = useState(() => {
    try { return window.localStorage.getItem('fet_reading_level') || 'B1'; } catch { return 'B1'; }
  });
  const [arabicOn, setArabicOn] = useState(() => {
    try {
      const stored = window.localStorage.getItem('fet_reading_arabic');
      return stored === null ? true : stored === '1';
    } catch { return true; }
  });

  const totalSteps = 5;
  const isLast = step === totalSteps - 1;

  // The user picked an exam paper before getting here. Carry the examId
  // through to reading-home so resolveReadingExam loads that specific paper.
  const goToHome = () => {
    let examId = null;
    try { examId = new URLSearchParams(window.location.search).get('examId'); } catch {}
    const target = examId
      ? `/?mode=reading-home&examId=${encodeURIComponent(examId)}`
      : '/?mode=reading-home';
    navigate(target, { replace: true });
  };

  const finish = () => {
    try {
      window.localStorage.setItem('fet_reading_onboarded', '1');
      window.localStorage.setItem('fet_reading_level', level);
      window.localStorage.setItem('fet_reading_arabic', arabicOn ? '1' : '0');
    } catch { /* private mode */ }
    goToHome();
  };
  const skip = () => {
    try { window.localStorage.setItem('fet_reading_onboarded', '1'); } catch {}
    goToHome();
  };
  const next = () => {
    if (isLast) finish();
    else setStep((s) => Math.min(totalSteps - 1, s + 1));
  };
  const back = () => setStep((s) => Math.max(0, s - 1));

  const phoneStyle = {
    width: '100%',
    maxWidth: 460,
    margin: '0 auto',
    background: theme.surface,
    borderRadius: 26,
    border: `1px solid ${theme.border}`,
    boxShadow: '0 30px 80px rgba(15,23,42,.18)',
    overflow: 'hidden',
    minHeight: 560,
    display: 'flex',
    flexDirection: 'column',
  };
  const h2Style = { fontSize: 24, fontWeight: 800, letterSpacing: '-.4px', margin: '0 0 6px', color: theme.text };
  const pStyle = { fontSize: 14, color: theme.body, lineHeight: 1.6, margin: 0 };

  const stepBody = () => {
    if (step === 0) {
      return (
        <div style={{ textAlign: 'center', padding: '12px 4px' }}>
          <div style={{ width: 88, height: 88, borderRadius: '50%', background: theme.accentSoftBg, color: theme.accent, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 38, margin: '14px 0 18px' }}>📖</div>
          <h2 style={h2Style}>FET Reading Practice</h2>
          <p style={pStyle}>Practise the 5 parts of your FET Reading exam at your own pace. Bilingual feedback (English + Arabic) on every question.</p>
        </div>
      );
    }
    if (step === 1) {
      return (
        <div>
          <h2 style={h2Style}>What you will practise</h2>
          <p style={pStyle}>Each part tests a different reading skill. The full exam has 30 questions in 45 minutes.</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 18 }}>
            {READING_PARTS_PREVIEW.map((p) => (
              <div key={p.n} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', background: theme.surfaceSoft, borderRadius: 12, border: `1px solid ${theme.border}` }}>
                <div style={{ width: 28, height: 28, borderRadius: 8, background: p.color, color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 800 }}>{p.n}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: theme.text }}>{p.title}</div>
                  <div style={{ fontSize: 12, color: theme.muted }}>{p.qs} questions · {p.marks} marks</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }
    if (step === 2) {
      const cards = [
        { icon: '🌿', title: 'Practice', items: ['No timer', 'Feedback after each answer', 'Tap words for translation', 'Training guides'] },
        { icon: '⏱', title: 'Exam', items: ['45-min timer', 'No feedback', 'No translations', 'Like the real exam'] },
      ];
      return (
        <div>
          <h2 style={h2Style}>Practice vs Exam</h2>
          <p style={pStyle}>Practice gives you hints and feedback. Exam is timed with no help — just like the real test.</p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 18 }}>
            {cards.map((c) => (
              <div key={c.title} style={{ padding: 14, background: theme.surfaceSoft, borderRadius: 14, border: `1px solid ${theme.border}` }}>
                <div style={{ fontSize: 22, marginBottom: 4 }} aria-hidden="true">{c.icon}</div>
                <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 6, color: theme.text }}>{c.title}</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {c.items.map((it) => (
                    <div key={it} style={{ fontSize: 12, color: theme.body, lineHeight: 1.45 }}>· {it}</div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }
    if (step === 3) {
      const opts = [
        { v: 'A2', t: 'A2 · Beginner', sub: 'Short texts, basic grammar' },
        { v: 'B1', t: 'B1 · Intermediate', sub: 'FET level (recommended)' },
        { v: 'B1+', t: 'B1+ · Upper', sub: 'More challenging inferences' },
      ];
      return (
        <div>
          <h2 style={h2Style}>Choose your starting level</h2>
          <p style={pStyle}>You can change this any time in settings. We use it to suggest what to practise.</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 18 }}>
            {opts.map((o) => {
              const on = level === o.v;
              return (
                <button
                  key={o.v}
                  type="button"
                  onClick={() => setLevel(o.v)}
                  style={{
                    textAlign: 'left',
                    padding: 14,
                    borderRadius: 14,
                    background: on ? theme.accentSoftBg : theme.surface,
                    border: `2px solid ${on ? theme.accent : theme.border}`,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                  }}
                >
                  <div style={{ width: 22, height: 22, borderRadius: 11, border: `2px solid ${on ? theme.accent : theme.border}`, background: on ? theme.accent : 'transparent', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    {on && <div style={{ width: 8, height: 8, borderRadius: 4, background: '#fff' }} />}
                  </div>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 800, color: theme.text }}>{o.t}</div>
                    <div style={{ fontSize: 12, color: theme.muted, marginTop: 2 }}>{o.sub}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      );
    }
    // step === 4
    return (
      <div>
        <h2 style={h2Style}>Arabic translations</h2>
        <p style={pStyle}>Should Arabic show by default in feedback and vocabulary? You can switch any time with the EN / AR toggle.</p>
        <div style={{ marginTop: 18, padding: 16, background: theme.surfaceSoft, borderRadius: 14, border: `1px solid ${theme.border}` }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 800, color: theme.text }}>Tap-for-translation</div>
              <div style={{ fontSize: 12, color: theme.muted, marginTop: 2 }}>Show Arabic by default in feedback &amp; vocab</div>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={arabicOn}
              onClick={() => setArabicOn((v) => !v)}
              style={{
                width: 50,
                height: 28,
                borderRadius: 14,
                background: arabicOn ? theme.accent : theme.border,
                position: 'relative',
                border: 'none',
                cursor: 'pointer',
                transition: 'background .18s ease',
              }}
            >
              <span style={{
                width: 22,
                height: 22,
                borderRadius: 11,
                background: '#fff',
                position: 'absolute',
                top: 3,
                left: arabicOn ? 25 : 3,
                boxShadow: '0 1px 3px rgba(0,0,0,.2)',
                transition: 'left .18s ease',
                display: 'block',
              }} />
            </button>
          </div>
          {arabicOn && (
            <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${theme.border}`, direction: 'rtl', textAlign: 'right' }}>
              <span lang="ar" style={{ fontSize: 14, fontFamily: "'Cairo', 'Tajawal', system-ui, sans-serif", color: theme.body }}>
                مثال: كلمة &quot;even&quot; تعني &quot;حتى&quot;
              </span>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: `linear-gradient(180deg, ${theme.pageBgAlt} 0%, ${theme.pageBgSoft} 100%)`,
      fontFamily: "'DM Sans', 'Outfit', sans-serif",
      color: theme.text,
      padding: isMobile ? '24px 16px 32px' : '40px 22px 56px',
    }}>
      <div style={{ maxWidth: 460, margin: '0 auto 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.16em', textTransform: 'uppercase', color: theme.muted }}>
          Reading · Welcome
        </div>
        <button
          type="button"
          onClick={skip}
          style={{
            background: 'transparent',
            border: 'none',
            color: theme.muted,
            fontSize: 13,
            fontWeight: 700,
            cursor: 'pointer',
            fontFamily: 'inherit',
            padding: '4px 6px',
          }}
        >
          Skip
        </button>
      </div>

      <div style={phoneStyle}>
        {/* Progress bar */}
        <div style={{ padding: '16px 18px 10px', display: 'flex', alignItems: 'center', gap: 6 }}>
          {Array.from({ length: totalSteps }).map((_, i) => (
            <div key={i} style={{
              flex: 1,
              height: 4,
              borderRadius: 2,
              background: i <= step ? theme.accent : theme.border,
              transition: 'background .2s ease',
            }} />
          ))}
        </div>

        <div style={{ flex: 1, padding: '14px 22px 8px', overflow: 'auto' }}>
          {stepBody()}
        </div>

        <div style={{ padding: '14px 18px 20px', display: 'flex', gap: 10, borderTop: `1px solid ${theme.border}` }}>
          {step > 0 && (
            <button
              type="button"
              onClick={back}
              style={{
                height: 44,
                padding: '0 16px',
                borderRadius: 12,
                border: `1px solid ${theme.border}`,
                background: theme.surface,
                color: theme.body,
                fontWeight: 700,
                fontSize: 13,
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              ← Back
            </button>
          )}
          <div style={{ flex: 1 }} />
          <button
            type="button"
            onClick={next}
            style={{
              height: 44,
              padding: '0 22px',
              borderRadius: 12,
              border: 'none',
              background: theme.ctaBg,
              color: theme.ctaText,
              fontWeight: 800,
              fontSize: 14,
              cursor: 'pointer',
              fontFamily: 'inherit',
              letterSpacing: '.02em',
            }}
          >
            {isLast ? 'Start learning →' : 'Next →'}
          </button>
        </div>
      </div>
    </div>
  );
}
